import { translate, translateApiError } from "@/lib/translation";
import { getCurrentLanguage } from "@/lib/i18n";
import { ApiError } from "@/lib/api";
import {
  API_BASE_URL,
  authorizedFetch,
  abortAllPendingRequests,
  clearLoggedInCookie,
} from "./base";
import { ApiUser } from "./types";

/**
 * Logs in a user and returns the access token.
 * Includes a retry mechanism for "Failed to fetch" errors.
 */
export async function login(email: string, password: string) {
  const details = new URLSearchParams();
  details.append("username", email);
  details.append("password", password);

  const maxRetries = 3;
  const retryDelayMs = 3000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: details,
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          translateApiError(data) ??
            (data.detail || translate("auth:clientErrors.loginFailed")),
        );
      }
      return await response.json();
    } catch (err: unknown) {
      if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
        if (attempt < maxRetries) {
          console.warn(
            `[Login] Attempt ${attempt} failed with "Failed to fetch". Retrying in ${retryDelayMs}ms... (Likely cold start)`,
          );
          await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
          continue;
        } else {
          throw new Error(
            translate("auth:clientErrors.serverUnreachableRetry"),
          );
        }
      } else {
        throw err;
      }
    }
  }
}

export async function sendOtp(
  email: string,
  turnstileToken?: string,
): Promise<{ message: string; otp_token: string }> {
  const response = await fetch(`${API_BASE_URL}/send-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      cf_turnstile_token: turnstileToken,
      // Picks the language of the verification email.
      language: getCurrentLanguage(),
    }),
  });

  if (!response.ok) {
    let message = translate("auth:clientErrors.sendCodeFailed");
    try {
      const data = await response.json();
      message =
        translateApiError(data) ?? (data.detail || data.message || message);
    } catch {
      /* ignore JSON parsing errors */
    }
    throw new ApiError(message, response.status);
  }

  return response.json();
}

export async function resendOtp(
  email: string,
): Promise<{ message: string; otp_token: string }> {
  const response = await fetch(`${API_BASE_URL}/resend-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, language: getCurrentLanguage() }),
  });

  if (!response.ok) {
    let message = translate("auth:clientErrors.resendCodeFailed");
    try {
      const data = await response.json();
      message =
        translateApiError(data) ?? (data.detail || data.message || message);
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  return response.json();
}

export async function register(
  email: string,
  password: string,
  preferredCurrency: string,
  country: string,
  otpToken: string,
  otpCode: string,
  refCode?: string,
  signupSource?: string,
): Promise<ApiUser> {
  const body: Record<string, string> = {
    username: email,
    password,
    preferred_display_currency: preferredCurrency,
    country,
    otp_token: otpToken,
    otp_code: otpCode,
    // Stored as the new account's language, so the app opens in the one used to sign up.
    language: getCurrentLanguage(),
  };
  if (refCode) body.ref_code = refCode;
  if (signupSource) body.signup_source = signupSource;

  const response = await fetch(`${API_BASE_URL}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = translate("auth:clientErrors.registrationFailed");
    try {
      const data = await response.json();
      message =
        translateApiError(data) ?? (data.detail || data.message || message);
    } catch {
      /* ignore JSON parsing errors */
    }
    throw new Error(message);
  }

  return response.json();
}

export async function joinWaitlist(
  email: string,
  turnstileToken?: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, cf_turnstile_token: turnstileToken }),
  });

  if (!response.ok) {
    let message = translate("auth:clientErrors.waitlistFailed");
    try {
      const data = await response.json();
      message =
        translateApiError(data) ?? (data.detail || data.message || message);
    } catch {
      /* ignore JSON parsing errors */
    }
    throw new Error(message);
  }
}

/**
 * Fetches the public countries list with retry logic to handle cold-start
 * delays on Koyeb's free tier (server may take up to ~30s to wake up).
 * Throws if all retries are exhausted so the caller can surface a message.
 */
export async function getCountriesPublic(): Promise<
  { code: string; name: string; default_currency: string }[]
> {
  const maxRetries = 5;
  const retryDelayMs = 4000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(`${API_BASE_URL}/countries/`);
      if (!response.ok) return [];
      return await response.json();
    } catch (err: unknown) {
      const isNetworkError =
        err instanceof TypeError && err.message.includes("Failed to fetch");
      if (isNetworkError && attempt < maxRetries) {
        console.warn(
          `[getCountriesPublic] Attempt ${attempt} failed. Retrying in ${retryDelayMs}ms... (Likely cold start)`,
        );
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      } else {
        throw err;
      }
    }
  }

  // Should be unreachable, but TypeScript needs this.
  throw new Error(translate("auth:clientErrors.serverUnreachable"));
}

export async function getMe(): Promise<ApiUser> {
  return authorizedFetch<ApiUser>("/users/me");
}

export async function forgotPassword(
  email: string,
): Promise<{ otp_token: string }> {
  const response = await fetch(`${API_BASE_URL}/forgot-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      translateApiError(data) ??
        (data.detail || translate("auth:clientErrors.requestFailed")),
    );
  }
  return response.json();
}

export async function resetPassword(
  email: string,
  otpToken: string,
  otpCode: string,
  newPassword: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/reset-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({
      email,
      otp_token: otpToken,
      otp_code: otpCode,
      new_password: newPassword,
    }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      translateApiError(data) ??
        (data.detail || translate("auth:clientErrors.passwordResetFailed")),
    );
  }
}

export async function logout(): Promise<void> {
  // Abort all pending requests before clearing the session cookies
  abortAllPendingRequests();

  try {
    await fetch(`${API_BASE_URL}/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
  } catch (err) {
    // Ignore errors — we'll clear cookies locally anyway
    console.warn(
      "[logout] backend call failed, clearing cookies locally:",
      err,
    );
  }

  // Ensure logged_in cookie is cleared locally even if backend call fails.
  // (access_token is httpOnly so we can't delete it from JS, but the backend should have cleared it)
  clearLoggedInCookie();
}

/** What Apple hands the page after a sign-in (see lib/appleSignIn.ts). */
export interface AppleCredential {
  identityToken: string;
  authorizationCode: string;
  nonce: string;
}

interface SocialSignupFields {
  preferredCurrency?: string;
  country?: string;
  refCode?: string;
  signupSource?: string;
}

/**
 * Shared by Google and Apple: posts the provider's proof of identity (plus, for a brand-new
 * account, the onboarding choices) and throws an error whose `isOnboardingRequired` flag tells
 * the page the person still has to pick country and currency.
 */
async function socialLogin(
  path: string,
  credentials: Record<string, string>,
  fields: SocialSignupFields,
  failureMessageKey: Parameters<typeof translate>[0],
): Promise<{ access_token: string; token_type: string }> {
  const body: Record<string, string> = {
    ...credentials,
    // Only used by the backend when this creates the account.
    language: getCurrentLanguage(),
  };
  if (fields.preferredCurrency) body.preferred_display_currency = fields.preferredCurrency;
  if (fields.country) body.country = fields.country;
  if (fields.refCode) body.ref_code = fields.refCode;
  if (fields.signupSource) body.signup_source = fields.signupSource;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(body),
    credentials: "include",
  });

  if (!response.ok) {
    let message = translate(failureMessageKey);
    let isOnboardingRequired = false;
    try {
      const data = await response.json();
      message = translateApiError(data) ?? (data.detail || message);
      isOnboardingRequired = message === "ONBOARDING_REQUIRED";
    } catch {
      /* ignore */
    }
    const error = new Error(message);
    if (isOnboardingRequired) {
      // TECH-DEBT: any — replace with a typed custom error class that carries isOnboardingRequired
      (error as any).isOnboardingRequired = true;
    }
    throw error;
  }

  return response.json();
}

export function loginWithGoogle(
  idToken: string,
  preferredCurrency?: string,
  country?: string,
  refCode?: string,
  signupSource?: string,
): Promise<{ access_token: string; token_type: string }> {
  return socialLogin(
    "/login/google",
    { id_token: idToken },
    { preferredCurrency, country, refCode, signupSource },
    "auth:clientErrors.googleSignInFailed",
  );
}

export function loginWithApple(
  credential: AppleCredential,
  preferredCurrency?: string,
  country?: string,
  refCode?: string,
  signupSource?: string,
): Promise<{ access_token: string; token_type: string }> {
  return socialLogin(
    "/login/apple",
    {
      identity_token: credential.identityToken,
      authorization_code: credential.authorizationCode,
      nonce: credential.nonce,
    },
    { preferredCurrency, country, refCode, signupSource },
    "auth:clientErrors.appleSignInFailed",
  );
}
